import React, { useState, useRef } from 'react';
import { CompanyRegistration } from '../../../types';

const MiEmpresa: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [companyData, setCompanyData] = useState<Partial<CompanyRegistration>>({
    razonSocial: 'Tecnología Avanzada S.A.',
    nombreFantasia: 'TechAvanzada',
    direccion: 'Av. Corrientes 1234',
    pais: 'Argentina',
    provincia: 'Buenos Aires',
    localidad: 'Ciudad Autónoma de Buenos Aires',
    codigoPostal: '1043',
    ddn: '+54',
    telefono1: '11-4567-8901',
    telefono2: '11-4567-8902',
    email: 'contacto@techavanzada.com',
    sitioWeb: 'www.techavanzada.com',
    tipoActividad: 'Tecnología',
    descripcion: 'Empresa líder en desarrollo de software y soluciones tecnológicas innovadoras.',
    rubros: 'Desarrollo de Software, Consultoría IT, Cloud Computing',
    marcas: 'TechAvanzada, CloudPro, DataSecure',
    comoNosConocio: 'Búsqueda en Google',
    contacto: 'Juan Pérez',
    usuario: 'juan.perez'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen válido.');
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
        return;
      }

      setLogoFile(file);
      
      // Crear URL para preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogo(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    // Aquí se guardarían los datos en el backend
    console.log('Guardando datos de empresa:', companyData);
    console.log('Logo file:', logoFile);
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Restaurar datos originales
    setIsEditing(false);
    // Restaurar logo original si existe
    // setLogo(originalLogo);
  };

  return (
    <div className="mi-empresa-section">
      <div className="section-header">
        <h2>Mi Empresa</h2>
        <p>Gestiona la información de tu empresa y mantén tus datos actualizados</p>
      </div>

      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Información de la Empresa</h3>
          <div className="card-actions">
            {!isEditing ? (
              <button 
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit"></i>
                Editar
              </button>
            ) : (
              <>
                <button 
                  className="btn btn-success"
                  onClick={handleSave}
                >
                  <i className="fas fa-save"></i>
                  Guardar
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  <i className="fas fa-times"></i>
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>

        <div className="company-info-grid">
          {/* Logo de la Empresa */}
          <div className="info-section">
            <h4>Logo de la Empresa</h4>
            <div className="logo-upload-section">
              <div className="logo-preview">
                {logo ? (
                  <div className="logo-container">
                    <img src={logo} alt="Logo de la empresa" className="company-logo" />
                    {isEditing && (
                      <button 
                        className="btn btn-danger btn-sm remove-logo-btn"
                        onClick={handleRemoveLogo}
                        title="Eliminar logo"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="logo-placeholder">
                    <i className="fas fa-building"></i>
                    <span>Sin logo</span>
                  </div>
                )}
              </div>
              
              {isEditing && (
                <div className="logo-upload-controls">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="logo-file-input"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="btn btn-outline-primary">
                    <i className="fas fa-upload"></i>
                    {logo ? 'Cambiar Logo' : 'Subir Logo'}
                  </label>
                  <p className="upload-hint">
                    Formatos: JPG, PNG, GIF. Tamaño máximo: 5MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Información Básica */}
          <div className="info-section">
            <h4>Información Básica</h4>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Razón Social</label>
                <input
                  type="text"
                  name="razonSocial"
                  value={companyData.razonSocial || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nombre de Fantasía</label>
                <input
                  type="text"
                  name="nombreFantasia"
                  value={companyData.nombreFantasia || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="info-section">
            <h4>Dirección</h4>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Dirección</label>
                <input
                  type="text"
                  name="direccion"
                  value={companyData.direccion || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">País</label>
                <input
                  type="text"
                  name="pais"
                  value={companyData.pais || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Provincia</label>
                <input
                  type="text"
                  name="provincia"
                  value={companyData.provincia || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Localidad</label>
                <input
                  type="text"
                  name="localidad"
                  value={companyData.localidad || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Código Postal</label>
                <input
                  type="text"
                  name="codigoPostal"
                  value={companyData.codigoPostal || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          {/* Contacto */}
          <div className="info-section">
            <h4>Información de Contacto</h4>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={companyData.email || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sitio Web</label>
                <input
                  type="url"
                  name="sitioWeb"
                  value={companyData.sitioWeb || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono Principal</label>
                <input
                  type="tel"
                  name="telefono1"
                  value={companyData.telefono1 || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono Secundario</label>
                <input
                  type="tel"
                  name="telefono2"
                  value={companyData.telefono2 || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          {/* Actividad Comercial */}
          <div className="info-section">
            <h4>Actividad Comercial</h4>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Tipo de Actividad</label>
                <input
                  type="text"
                  name="tipoActividad"
                  value={companyData.tipoActividad || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Rubros</label>
                <input
                  type="text"
                  name="rubros"
                  value={companyData.rubros || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea
                name="descripcion"
                value={companyData.descripcion || ''}
                onChange={handleInputChange}
                className="form-input form-textarea"
                disabled={!isEditing}
                rows={4}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Marcas</label>
              <input
                type="text"
                name="marcas"
                value={companyData.marcas || ''}
                onChange={handleInputChange}
                className="form-input"
                disabled={!isEditing}
              />
            </div>
          </div>

          {/* Información Adicional */}
          <div className="info-section">
            <h4>Información Adicional</h4>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Contacto Principal</label>
                <input
                  type="text"
                  name="contacto"
                  value={companyData.contacto || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Usuario</label>
                <input
                  type="text"
                  name="usuario"
                  value={companyData.usuario || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiEmpresa;
